import jwtDecode from 'jwt-decode'
import axios from 'axios'

import config from '../../config'

const AUTH_SESSION_KEY = 'infotourist_core_user'
const AUTH_SESSION_KEY_INFOTOOLS = 'infotools_core_user'

const clearAuthAndRedirect = () => {
  sessionStorage.removeItem(AUTH_SESSION_KEY)
  sessionStorage.removeItem(AUTH_SESSION_KEY_INFOTOOLS)
  delete axios.defaults.headers.common.Authorization

  const currentPath = window.location.pathname || ''
  const infotoolsRoutes = ['/infotools', '/infoData', '/surveyator', '/preview-categories', '/preview-documents']
  const isInfotoolsRoute = infotoolsRoutes.some(route => currentPath.startsWith(route))

  window.location.replace(isInfotoolsRoute ? '/infotools/login' : '/account/login')
}

const shouldHandleUnauthorizedRedirect = (error) => {
  const status = error?.response?.status
  if (status !== 401) return false

  const requestUrl = `${error?.config?.url || ''}`
  return !requestUrl.includes('/login/') && !requestUrl.includes('/infotools/login/')
}

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json'
axios.defaults.baseURL = config.API_URL

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    let message
    const customCode = error.response?.data?.customCode // Accede al código interno
    // console.log(error.response.data.error)
    const status = error?.response?.status

    switch (status) {
      case 401:
        message = error?.response?.data?.error || 'Invalid credentials'
        if (shouldHandleUnauthorizedRedirect(error)) {
          clearAuthAndRedirect()
        }
        break
      case 403:
        // Si hay un código interno, úsalo para mensajes personalizados
        if (customCode === 4401) {
          message = 'Access Denied'
        } else {
          message = 'Access Forbidden'
        }
        break
      case 404:
        message = 'Sorry! the data you are looking for could not be found'
        break
      default: {
        message = error.response?.data?.error || error.response?.data?.message || error.message || error
      }
    }
    return Promise.reject(message)
  }
)

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  if (token) axios.defaults.headers.common.Authorization = 'Bearer ' + token
  else delete axios.defaults.headers.common.Authorization
}

const setAuthorizationInfotools = (token) => {
  if (token) axios.defaults.headers.common.Authorization = 'Bearer ' + token
  else delete axios.defaults.headers.common.Authorization
}

const getUserFromSession = () => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY)
  return user ? (typeof user === 'object' ? user : JSON.parse(user)) : null
}

const getUserInfotoolsFromSession = () => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY_INFOTOOLS)
  return user ? (typeof user === 'object' ? user : JSON.parse(user)) : null
}

class APICore {
  /**
     * Fetches data from given url
     */
  get = (url, params) => {
    let response
    if (params) {
      const queryString = params
        ? Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&')
        : ''
      response = axios.get(`${url}?${queryString}`, params)
    } else {
      response = axios.get(`${url}`, params)
    }
    return response
  }

  getFile = (url, params) => {
    let response
    if (params) {
      const queryString = params
        ? Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&')
        : ''
      response = axios.get(`${url}?${queryString}`, { responseType: 'blob' })
    } else {
      response = axios.get(`${url}`, { responseType: 'blob' })
    }
    return response
  }

  getMultiple = (urls, params) => {
    const reqs = []
    let queryString = ''
    if (params) {
      queryString = params
        ? Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&')
        : ''
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`))
    }
    return axios.all(reqs)
  }

  /**
     * post given data to url
     */
  create = (url, data, config = {}) => {
    return axios.post(url, data, config)
  }

  /**
     * Updates patch data
     */
  updatePatch = (url, data) => {
    return axios.patch(url, data)
  }

  /**
     * Updates data
     */
  update = (url, data) => {
    return axios.put(url, data)
  }

  /**
     * Deletes data
     */
  delete = (url, data) => {
    return axios.delete(url, { data })
  }

  /**
     * post given data to url with file
     */
  // createWithFile = (url, data) => {
  //   const formData = new FormData()
  //   for (const k in data) {
  //     formData.append(k, data[k])
  //   }

  //   const config = {
  //     headers: {
  //       ...axios.defaults.headers,
  //       'content-type': 'multipart/form-data'
  //     }
  //   }
  //   return axios.post(url, formData, config)
  // }

  createWithFile = (url, data) => {
    const formData = new FormData()
    const multiKey = data.multifile || false
    for (const k in data) {
      if (k !== multiKey) formData.append(k, data[k])
      else {
        for (const file of data[k]) {
          formData.append(multiKey, file)
        }
      }
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        'content-type': 'multipart/form-data'
      }
    }
    return axios.post(url, formData, config)
  }

  //   /**
  //      * post given data to url with file
  //      */
  //   updateWithFile = (url, data) => {
  //     const formData = new FormData()
  //     for (const k in data) {
  //       formData.append(k, data[k])
  //     }

  //     const config = {
  //       headers: {
  //         ...axios.defaults.headers,
  //         'content-type': 'multipart/form-data'
  //       }
  //     }
  //     return axios.put(url, formData, config)
  //   }

  /**
     * post given data to url with file
     */
  updateWithFile = (url, data) => {
    const formData = new FormData()
    const multiKey = data.multifile || false
    for (const k in data) {
      if (k !== multiKey) formData.append(k, data[k])
      else {
        for (const file of data[k]) {
          formData.append(multiKey, file)
        }
      }
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        'content-type': 'multipart/form-data'
      }
    }
    return axios.put(url, formData, config)
  }

  updatePatchWithFile = (url, data) => {
    const formData = new FormData()
    for (const k in data) {
      formData.append(k, data[k])
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        'content-type': 'multipart/form-data'
      }
    }
    return axios.patch(url, formData, config)
  }

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser()
    if (!user || (user && !user.token)) {
      return false
    }
    const decoded = jwtDecode(user.token)
    const currentTime = Date.now() / 1000
    if (decoded.exp < currentTime) {
      console.warn('access token expired')
      return false
    } else {
      return true
    }
  }

  isUserInfotoolsAuthenticated = () => {
    const user = this.getLoggedInUserInfotools()
    if (!user || (user && !user.token)) {
      return false
    }
    const decoded = jwtDecode(user.token)
    const currentTime = Date.now() / 1000
    if (decoded.exp < currentTime) {
      console.warn('access token expired')
      return false
    } else {
      return true
    }
  }

  setLoggedInUser = (session) => {
    if (session) sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session))
    else {
      sessionStorage.removeItem(AUTH_SESSION_KEY)
    }
  }

  setLoggedInUserInfotools = (session) => {
    if (session) sessionStorage.setItem(AUTH_SESSION_KEY_INFOTOOLS, JSON.stringify(session))
    else {
      sessionStorage.removeItem(AUTH_SESSION_KEY_INFOTOOLS)
    }
  }

  // Actualiza el token en la sesión del usuario manteniendo la información del usuario
  setUpdatedToken = (dataTokenAndDestination) => {
    const { newToken, destination, owner } = dataTokenAndDestination
    const userInfo = sessionStorage.getItem(AUTH_SESSION_KEY)
    if (userInfo) {
      const user = JSON.parse(userInfo)
      user.destination = destination
      user.token = newToken
      if (owner) {
        user.owner = owner?.name
        user.ownerId = owner?.id
      }
      this.setLoggedInUser(user)
    }
  }

  /**
     * Returns the logged in user
     */
  getLoggedInUser = () => {
    return getUserFromSession()
  }

  getLoggedInUserInfotools = () => {
    return getUserInfotoolsFromSession()
  }

  getToken = () => {
    const user = getUserFromSession()
    return user ? user.token : null
  }

  setUserInSession = (modifiedUser) => {
    const userInfo = sessionStorage.getItem(AUTH_SESSION_KEY)
    if (userInfo) {
      const { token, user } = JSON.parse(userInfo)
      this.setLoggedInUser({ token, ...user, ...modifiedUser })
    }
  }

  setUserInfotoolsInSession = (modifiedUser) => {
    const userInfo = sessionStorage.getItem(AUTH_SESSION_KEY_INFOTOOLS)
    if (userInfo) {
      const { token, user } = JSON.parse(userInfo)
      this.setLoggedInUserInfotools({ token, ...user, ...modifiedUser })
    }
  }
}

/*
Check if token available in session
*/
const user = getUserFromSession()
if (user) {
  const { token } = user
  if (token) {
    setAuthorization(token)
  }
}

const userInfotools = getUserInfotoolsFromSession()
if (userInfotools) {
  const { token } = userInfotools
  if (token) {
    setAuthorizationInfotools(token)
  }
}

export { APICore, setAuthorization, setAuthorizationInfotools }
